import React from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';

const SeniorCare = () => {
  // ── Key Programmes data ──
  const programmes = [
    {
      icon: "🧺",
      title: "Daily Essentials Support",
      desc: "Providing regular supplies of nutritious food, groceries, hygiene products, and other daily necessities to senior citizens who lack financial or family support.",
    },
    {
      icon: "💊",
      title: "Medical & Health Support",
      desc: "Arranging health check-ups, providing essential medicines, covering treatment costs, and organising medical camps specifically for the elderly in underserved communities.",
    },
    {
      icon: "💛",
      title: "Emotional & Social Care",
      desc: "Combating loneliness through regular visits, elderly social gatherings, festival celebrations, and a dedicated helpline for emotional support and companionship.",
    },
    {
      icon: "♿",
      title: "Mobility & Accessibility Aids",
      desc: "Providing walkers, wheelchairs, hearing aids, spectacles, and other assistive devices to help elderly individuals maintain independence and quality of life.",
    },
    {
      icon: "🏠",
      title: "Home Visits & Caregiving",
      desc: "Dedicated volunteers and caregivers visiting elderly individuals at home—assisting with daily chores, personal care, and offering a comforting presence.",
    },
    {
      icon: "⚖️",
      title: "Awareness & Legal Aid",
      desc: "Educating seniors about their rights, government welfare schemes, pension benefits, and providing legal assistance to address issues like property disputes and wills.",
    },
  ];

  // ── Impact Stats ──
  const stats = [
    { number: "2,400+", label: "Elders Supported" },
    { number: "1,800+", label: "Medical Check-ups" },
    { number: "6,200+", label: "Essential Supplies Delivered" },
    { number: "950+", label: "Volunteer Hours" },
  ];

  // ── How You Can Help ──
  const helpOptions = [
    { icon: "💝", title: "Donate", desc: "Support our programmes with a one-time or monthly donation." },
    { icon: "🤝", title: "Volunteer", desc: "Spend time with elders—your presence is the greatest gift." },
    { icon: "📦", title: "Sponsor", desc: "Sponsor an elderly person's daily essentials or medical care." },
  ];

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="senior-hero relative text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-user-friends mr-2"></i> Our Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Care for Senior Citizens
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              Honouring our elders with love, dignity, and compassionate support
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== OVERVIEW ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">Caring for Our Elders</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              At Venkatadri Seva Trust, we believe that our senior citizens are the pillars of our society—the
              bearers of wisdom, tradition, and invaluable life experiences. Yet, many elders today face neglect,
              loneliness, and financial insecurity. Our <strong>Care for Senior Citizens</strong> programme is a
              heartfelt initiative to bring back the warmth, respect, and care that every elderly person deserves.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              Through a comprehensive range of services—from providing daily essentials and medical assistance
              to organising community gatherings and emotional support—we strive to ensure that no senior feels
              abandoned or forgotten. We are committed to creating a world where growing old is a time of peace,
              happiness, and dignity.
            </p>
          </div>
        </div>
      </section>

      {/* ========== VISION & MISSION ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">👁️</div>
              <h3 className="text-2xl font-bold mb-3">Our Vision</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                To create a society where every senior citizen lives with respect, joy, and security—where their
                golden years are filled with love, companionship, and the care they so rightfully deserve.
              </p>
            </div>
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-3">Our Mission</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                To reach out to elderly individuals in need, provide holistic support—physical, emotional, and
                social—and create a community of compassion that honours and uplifts our senior citizens.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== KEY PROGRAMMES ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Key Programmes</h2>
            <p className="text-yellow-100 mt-2 text-lg">Bringing comfort, care, and smiles to our elders</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {programmes.map((programme, idx) => (
              <div
                key={idx}
                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
              >
                <div className="service-icon"><span>{programme.icon}</span></div>
                <h3 className="text-xl font-semibold text-gray-800">{programme.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{programme.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== IMPACT / STATS ========== */}
      {/* <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Impact</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center text-white">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-bold text-yellow-300">{stat.number}</div>
                <p className="text-yellow-100/80 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ========== HOW YOU CAN HELP ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">How You Can Help</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
              <p className="text-gray-600 mt-4 text-lg">
                Every contribution—big or small—makes a world of difference to a senior in need.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {helpOptions.map((option, idx) => (
                <div key={idx} className="text-center p-4 rounded-lg bg-gray-50 hover:bg-yellow-50 transition-smooth">
                  <div className="text-4xl text-primary mb-3">{option.icon}</div>
                  <h4 className="font-bold text-gray-800">{option.title}</h4>
                  <p className="text-gray-600 text-sm mt-1">{option.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="/donate"
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-smooth inline-block"
              >
                <i className="fas fa-hand-holding-heart mr-2"></i> Support Our Elders
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIAL ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="text-6xl mb-4">“</div>
            <p className="text-xl md:text-2xl font-light leading-relaxed italic">
              The volunteers from Venkatadri Seva Trust visit me every week. They bring not just groceries and
              medicine, but so much love and warmth. I no longer feel alone. They are like my own family.
            </p>
            <div className="mt-6">
              <p className="font-semibold text-yellow-200">— Lakshmamma, 78</p>
              <p className="text-yellow-100/70 text-sm">Bengaluru</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== JOIN OUR MISSION ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Mission</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-yellow-100/90 italic mb-6">
              "To care for those who once cared for us is one of the highest honours."
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Your generous support—through donations, volunteering, or partnerships—helps us reach more elderly
              lives and build a brighter future for those in need.
            </p>
            <p className="text-xl text-yellow-100 font-light mb-8">
              Together, let us make compassion a way of life and create a better world for generations to come.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/donate"
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-smooth"
              >
                <i className="fas fa-heart mr-2"></i> Donate Now
              </a>
              <a
                href="/volunteer"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-smooth"
              >
                <i className="fas fa-hand-peace mr-2"></i> Volunteer
              </a>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default SeniorCare;